'use client';

import React, { useEffect } from 'react';
import { EcommerceConfig } from '@/lib/types';
import { ThemeProvider } from './ThemeProvider';
import { AuthProvider } from './AuthProvider';
import { CartProvider } from './CartProvider';
import { WishlistProvider } from './WishlistProvider';
import { BasePathProvider } from './BasePathProvider';
import { DiscountProvider } from './DiscountProvider';
import { initializeApiAdapter } from '@/lib/api-adapter';
import { QueryClientProvider } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { NotificationProvider } from './NotificationProvider';
import { NotificationCenterProvider } from './NotificationCenterProvider';
import { NotificationDrawer } from '@/components/NotificationDrawer';
import { CategoriesProvider } from './CategoriesProvider';
import { StoreProvider } from '@/context/StoreContext';

interface EcommerceProviderProps {
  config: EcommerceConfig;
  children: React.ReactNode;
  withToaster?: boolean;
  basePath?: string;
}

export function EcommerceProvider({ config, children, withToaster = true, basePath = '' }: EcommerceProviderProps) {
  // Initialize API adapter with store configuration synchronously
  // This ensures setting up the real backend APIs before children render and fire their effects
  React.useMemo(() => {
    initializeApiAdapter(config);
  }, [config]);

  const [client] = React.useState(
    new QueryClient({ defaultOptions: { queries: { staleTime: 5000 } } })
  );
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider config={config}>
        <BasePathProvider basePath={basePath}>
          <AuthProvider>
            <NotificationProvider>
              <NotificationCenterProvider>
                <CategoriesProvider>
                  <WishlistProvider>
                    <CartProvider>
                      <StoreProvider>
                        <DiscountProvider>
                          {children}
                        </DiscountProvider>
                      </StoreProvider>
                    </CartProvider>
                  </WishlistProvider>
                </CategoriesProvider>
              </NotificationCenterProvider>
            </NotificationProvider>
          </AuthProvider>
        </BasePathProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
